import { useEffect, useRef, useState } from 'react';

import { type HTMLArkProps, ark } from '@ark-ui/react';

import type { ActionColor, Scale2 } from '../../lib/types';
import { cn } from '../../lib/utils';

type TabNavRootProps = Omit<HTMLArkProps<'nav'>, 'color'> & {
	size?: Scale2;
	color?: ActionColor;
};

type TabNavLinkProps = HTMLArkProps<'a'> & {
	active?: boolean;
};

interface TabNavIndicatorState {
	x: number;
	width: number;
	visible: boolean;
}

function TabNavRoot({
	className,
	size = '2',
	color = 'accent',
	children,
	...props
}: TabNavRootProps) {
	const listRef = useRef<HTMLDivElement | null>(null);
	const [indicator, setIndicator] = useState<TabNavIndicatorState>({
		x: 0,
		width: 0,
		visible: false
	});

	useEffect(() => {
		const list = listRef.current;
		if (!list) return;

		let frame = 0;

		const syncIndicator = () => {
			const activeLink = list.querySelector<HTMLElement>('.pt-tab-nav-link[data-active]');

			if (!activeLink) {
				setIndicator((current) => (current.visible ? { ...current, visible: false } : current));
				return;
			}

			const next = {
				x: activeLink.offsetLeft,
				width: activeLink.offsetWidth,
				visible: true
			};

			setIndicator((current) =>
				current.x === next.x && current.width === next.width && current.visible === next.visible
					? current
					: next
			);
		};

		const scheduleSync = () => {
			if (frame) window.cancelAnimationFrame(frame);
			frame = window.requestAnimationFrame(syncIndicator);
		};

		scheduleSync();

		const mutationObserver = new MutationObserver(scheduleSync);
		mutationObserver.observe(list, {
			subtree: true,
			childList: true,
			attributes: true,
			attributeFilter: ['data-active', 'class']
		});

		const resizeObserver = new ResizeObserver(scheduleSync);
		resizeObserver.observe(list);
		for (const link of list.querySelectorAll<HTMLElement>('.pt-tab-nav-link')) {
			resizeObserver.observe(link);
		}

		return () => {
			if (frame) window.cancelAnimationFrame(frame);
			mutationObserver.disconnect();
			resizeObserver.disconnect();
		};
	}, [children]);

	return (
		<ark.nav
			className={cn('pt-tab-nav', className)}
			data-size={size}
			data-accent-color={color}
			{...props}
		>
			<ark.div ref={listRef} className="pt-tab-nav-list px-3">
				{children}
				<ark.span
					aria-hidden="true"
					className="pt-tab-nav-indicator"
					style={{
						width: indicator.width ? `${indicator.width}px` : undefined,
						transform: `translateX(${indicator.x}px)`,
						opacity: indicator.visible ? 1 : 0
					}}
				/>
			</ark.div>
		</ark.nav>
	);
}

function TabNavLink({ className, active, ...props }: TabNavLinkProps) {
	return (
		<ark.a
			className={cn('pt-tab-nav-link', className)}
			data-active={active ? '' : undefined}
			{...props}
		/>
	);
}

const TabNav = Object.assign(TabNavRoot, {
	Root: TabNavRoot,
	Link: TabNavLink
});

export { TabNav, type TabNavRootProps, type TabNavLinkProps };
